import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import type { InviteScope } from '../../lib/sharing-types';
import { Button } from '../../ui';
import { shareKeys } from './keys';

/** The invitations still waiting at a scope (not accepted, not revoked): who, which role, until when, and Revoke. */
export function PendingInvitations({ scope }: { scope: InviteScope }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const invitations = useQuery({ queryKey: shareKeys.invitations(scope), queryFn: () => api.listInvitations(scope) });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeInvitation(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: shareKeys.invitations(scope) }),
  });
  const pending = (invitations.data ?? []).filter((i) => !i.acceptedAt && !i.revokedAt);
  if (pending.length === 0) return null;
  return (
    <section aria-label={t('sharing.pending')} className="flex flex-col gap-2">
      <h3 className="font-sans text-sm font-semibold">{t('sharing.pending')}</h3>
      <ul className="flex flex-col gap-2">
        {pending.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center gap-2 font-sans text-sm">
            <span className="min-w-0 flex-1 truncate">{i.email}</span>
            <span className="text-fg-muted">{t(`sharing.roles.${i.role}`, { defaultValue: i.role })}</span>
            <span className="text-fg-muted">
              {Date.parse(i.expiresAt) < Date.now() ? t('sharing.expired') : t('sharing.expiresOn', { date: new Date(i.expiresAt).toLocaleDateString(i18n.language) })}
            </span>
            <Button onClick={() => revoke.mutate(i.id)}>{t('sharing.revoke')}</Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
